import { formatIsoDay, formatMoney, formatNumber, formatPct, formatRate, formatTRY } from "@/lib/format";
import { amountInWords } from "@/lib/amountWords";
import { PAYMENTS } from "@/lib/orders";
import { CompanyHead, Sign } from "@/components/print/CompanyHead";
import { BRAND } from "@/lib/brand";

const r2 = (v) => Math.round(v * 100) / 100;

export const vatBreakdown = (lines) => {
  const by = {};
  lines.forEach((l) => {
    const x = (by[l.vat_rate] ||= { rate: l.vat_rate, base: 0, vat: 0 });
    x.base += l.line_net;
    x.vat += l.vat_amount;
  });
  return Object.values(by).sort((a, b) => a.rate - b.rate).map((x) => ({ ...x, base: r2(x.base), vat: r2(x.vat) }));
};

const Party = ({ cu }) => (
  <div className="border border-slate-300 p-2 text-[12px]" data-testid="invoice-customer">
    <div className="text-[10px] uppercase text-slate-500">Sayın</div>
    <div className="font-semibold">{cu.title}</div>
    <div>{[cu.address, cu.district, cu.city].filter(Boolean).join(" · ")}</div>
    <div>V.D.: {cu.tax_office || "—"} · V.No/TCKN: {cu.tax_no || "—"}</div>
    <div className="text-slate-500">Cari Kodu {cu.code}{cu.phone ? ` · ${cu.phone}` : ""}</div>
  </div>
);

export default function InvoiceDoc({ o, meta }) {
  const cur = o.currency;
  const vat = vatBreakdown(o.lines);
  const info = [["Fatura No", o.invoice_no], ["Fatura Tarihi", formatIsoDay(o.invoice_date)], ["Vade Tarihi", formatIsoDay(o.due_date)],
    ["Sipariş No", o.order_no], ["Ödeme", PAYMENTS[o.payment_method]], ["Döviz", cur === "TRY" ? "TL" : `${cur} · Kur ${formatRate(o.fx_rate)}`]];
  return (
    <div data-testid="invoice-doc">
      <CompanyHead c={meta.company} brand={BRAND} title="SATIŞ FATURASI" no={o.invoice_no} sub={`Fatura tarihi ${formatIsoDay(o.invoice_date)}`} />
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Party cu={meta.customer} />
        <table className="text-[12px] w-full border border-slate-300" data-testid="invoice-info"><tbody>
          {info.map(([k, v]) => <tr key={k} className="border-b border-slate-200"><td className="px-2 py-0.5 text-slate-500">{k}</td><td className="px-2 font-mono text-right">{v}</td></tr>)}
        </tbody></table>
      </div>
      <table className="w-full text-[11px] border-collapse" data-testid="invoice-lines">
        <thead><tr className="border-b border-slate-900 text-left"><th className="py-1">#</th><th>Kod</th><th>Ürün</th><th className="text-right">Adet</th><th className="text-right">Birim Fiyat</th>
          <th className="text-right">İsk.</th><th className="text-right">KDV</th><th className="text-right">Net Tutar</th></tr></thead>
        <tbody>{o.lines.map((l, i) => (
          <tr key={`${l.sku}-${i}`} className="border-b border-slate-200" data-testid={`invoice-line-${i}`}><td className="py-1">{i + 1}</td><td className="font-mono">{l.sku}</td><td>{l.name}</td>
            <td className="text-right font-mono">{formatNumber(l.qty)} {l.unit}</td><td className="text-right font-mono">{formatMoney(l.unit_price, cur)}</td>
            <td className="text-right font-mono">{formatPct(l.discount_pct)}</td><td className="text-right font-mono">%{l.vat_rate}</td>
            <td className="text-right font-mono">{formatMoney(l.line_net, cur)}</td></tr>))}</tbody>
      </table>
      <div className="flex justify-between gap-6 mt-3">
        <table className="text-[11px] self-start border border-slate-300" data-testid="invoice-vat-table">
          <thead><tr className="bg-slate-100"><th className="px-2 py-0.5 text-left">KDV Oranı</th><th className="px-2 text-right">Matrah</th><th className="px-2 text-right">KDV</th></tr></thead>
          <tbody>{vat.map((v) => <tr key={v.rate} data-testid={`invoice-vat-${v.rate}`}><td className="px-2">%{v.rate}</td><td className="px-2 text-right font-mono">{formatMoney(v.base, cur)}</td><td className="px-2 text-right font-mono">{formatMoney(v.vat, cur)}</td></tr>)}</tbody>
        </table>
        <div className="w-80 text-[12px] space-y-0.5" data-testid="invoice-totals">
          {[["Ara Toplam (Brüt)", o.subtotal], ["İskonto", -o.discount_total], ["KDV Matrahı", o.net_total], ...vat.map((v) => [`Hesaplanan KDV %${v.rate}`, v.vat]), ["Toplam KDV", o.vat_total]].map(([k, v]) => (
            <div key={k} className="flex justify-between"><span>{k}</span><span className="font-mono">{formatMoney(v, cur)}</span></div>))}
          <div className="flex justify-between font-bold border-t border-slate-900 pt-1 text-[13px]"><span>Genel Toplam</span><span className="font-mono" data-testid="invoice-grand-total">{formatMoney(o.grand_total, cur)}</span></div>
          {cur !== "TRY" && <div className="flex justify-between text-slate-600"><span>TL Karşılığı (Kur {formatRate(o.fx_rate)})</span><span className="font-mono" data-testid="invoice-try-total">{formatTRY(o.grand_total_try)}</span></div>}
        </div>
      </div>
      <div className="mt-3 border border-slate-300 p-2 text-[12px]" data-testid="invoice-amount-words"><b>Yazıyla:</b> {amountInWords(o.grand_total, cur)}</div>
      {meta.company?.iban && <div className="mt-2 text-[11px] text-slate-600">Ödeme için IBAN: <span className="font-mono">{meta.company.iban}</span></div>}
      <Sign labels={["Teslim Eden", "Teslim Alan"]} />
    </div>
  );
}
